import React, { useState } from 'react';
import {
  LifeBuoy,
  ShieldAlert,
  Clock,
  CheckCircle2,
  AlertTriangle,
  FileSpreadsheet,
  Cpu,
  BrainCircuit,
  MessageSquare,
  Users,
  Search,
  Filter,
  Eye,
  Send,
  Sparkles,
  Layers,
  BarChart3,
  Globe,
  Award,
  Lock,
  ArrowUpRight,
  TrendingUp,
  CreditCard,
  Building2,
  Sliders
} from 'lucide-react';
import { AppView } from '../../types';
import { supportEngine } from '../../services/support/supportEngine';
import {
  SupportCase,
  SupportQueue,
  FinancialCalculationEscalation,
  KnowledgeArticle,
  VoiceOfCustomerTheme,
  KnowledgeGap
} from '../../services/support/supportTypes';

interface ChiefCustomerExperienceOfficerCommandCenterProps {
  onNavigateToView?: (view: AppView) => void;
}

type TabType =
  | 'overview'
  | 'cases'
  | 'queues'
  | 'financial-escalations'
  | 'ai-copilot'
  | 'knowledge-gaps'
  | 'voc-analytics'
  | 'audit-log';

export const ChiefCustomerExperienceOfficerCommandCenter: React.FC<ChiefCustomerExperienceOfficerCommandCenterProps> = ({
  onNavigateToView
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [selectedCase, setSelectedCase] = useState<SupportCase | null>(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [replyType, setReplyType] = useState<'AGENT' | 'INTERNAL_NOTE'>('AGENT');
  const [searchFilter, setSearchFilter] = useState('');
  const [queueFilter, setQueueFilter] = useState<string>('ALL');

  const cases = supportEngine.getCases();
  const queues = supportEngine.getQueues();
  const financialEscalations = supportEngine.getFinancialEscalations();
  const knowledgeGaps = supportEngine.getKnowledgeGaps();
  const vocThemes = supportEngine.getVocThemes();
  const auditLog = supportEngine.getAuditLog();
  const csatList = supportEngine.getCsatResponses();
  const npsData = supportEngine.getNpsData();

  // Metrics
  const totalOpenCases = cases.filter(c => c.status !== 'CLOSED' && c.status !== 'RESOLVED').length;
  const criticalCases = cases.filter(c => c.priority === 'P0_CRITICAL' || c.priority === 'P1_HIGH').length;
  const avgCsat = (csatList.reduce((acc, curr) => acc + curr.score, 0) / (csatList.length || 1)).toFixed(1);

  const filteredCases = cases.filter(c => {
    const matchesSearch =
      searchFilter === '' ||
      c.publicId.toLowerCase().includes(searchFilter.toLowerCase()) ||
      c.customerName.toLowerCase().includes(searchFilter.toLowerCase()) ||
      c.subject.toLowerCase().includes(searchFilter.toLowerCase()) ||
      c.domain.toLowerCase().includes(searchFilter.toLowerCase());

    const matchesQueue = queueFilter === 'ALL' || c.assignedQueue === queueFilter;
    return matchesSearch && matchesQueue;
  });

  const handleSendReply = (caseId: string) => {
    if (!replyMessage.trim()) return;
    const updated = supportEngine.addMessage(
      caseId,
      replyType,
      replyType === 'AGENT' ? 'Alex Rivera (Support Lead)' : 'Internal Audit Specialist',
      replyMessage
    );
    if (updated) {
      setSelectedCase(updated);
      setReplyMessage('');
    }
  };

  const handleStatusChange = (caseId: string, status: SupportCase['status']) => {
    const updated = supportEngine.updateCaseStatus(caseId, status, 'Alex Rivera (CXO Ops)');
    if (updated) {
      setSelectedCase(updated);
    }
  };

  const handleApplyAICopilot = (caseItem: SupportCase) => {
    const suggestion = supportEngine.generateAICopilotSuggestion(caseItem);
    setReplyMessage(suggestion.suggestedReply);
    setReplyType('AGENT');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[#0F172A] pb-24">
      {/* Top Banner */}
      <div className="bg-slate-900 text-white border-b border-slate-800 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-600 rounded-xl text-white">
                <LifeBuoy className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl font-black tracking-tight">
                  Chief Customer Experience Officer (CXO) Command Center
                </h1>
                <p className="text-xs sm:text-sm text-slate-400">
                  Global Support Operations • Financial Escalations • AI Copilot • SLAs • Voice of Customer • QA
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {onNavigateToView && (
              <button
                onClick={() => onNavigateToView('help-center')}
                className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-xs font-bold rounded-xl transition-colors border border-slate-700 cursor-pointer flex items-center gap-1.5"
              >
                <Eye className="w-4 h-4 text-blue-400" />
                View Public Help Center
              </button>
            )}
            <div className="px-3 py-1.5 bg-emerald-950/80 border border-emerald-700/50 rounded-xl text-emerald-400 text-xs font-bold flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              SLA Attainment 99.4%
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b border-[#E2E8F0] sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-1 overflow-x-auto py-2 text-xs font-bold">
          {[
            { id: 'overview', label: 'Executive CX Overview', icon: Layers },
            { id: 'cases', label: `Support Cases (${cases.length})`, icon: MessageSquare },
            { id: 'queues', label: `Queues (${queues.length})`, icon: Sliders },
            { id: 'financial-escalations', label: `Financial Escalations (${financialEscalations.length})`, icon: FileSpreadsheet },
            { id: 'ai-copilot', label: 'AI Support Copilot & RAG', icon: BrainCircuit },
            { id: 'knowledge-gaps', label: `Knowledge Gaps (${knowledgeGaps.length})`, icon: Search },
            { id: 'voc-analytics', label: 'Voice of Customer & CSAT/NPS', icon: BarChart3 },
            { id: 'audit-log', label: 'Support Audit Trail', icon: Lock }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as TabType)}
                className={`px-3.5 py-2 rounded-xl flex items-center gap-2 transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-[#475569] hover:bg-slate-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* ==================================================== */}
        {/* 1. OVERVIEW TAB */}
        {/* ==================================================== */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Top KPI Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-xs">
                <div className="text-[11px] font-bold text-[#64748B] uppercase">Open Cases</div>
                <div className="text-2xl font-black text-[#0F172A] mt-1">{totalOpenCases}</div>
                <div className="text-[10px] text-emerald-600 font-bold mt-1">42% auto-deflected</div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-xs">
                <div className="text-[11px] font-bold text-[#64748B] uppercase">Critical (P0/P1)</div>
                <div className="text-2xl font-black text-rose-600 mt-1">{criticalCases}</div>
                <div className="text-[10px] text-rose-700 font-bold mt-1">Direct L2/L3 routing</div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-xs">
                <div className="text-[11px] font-bold text-[#64748B] uppercase">Median First Response</div>
                <div className="text-2xl font-black text-blue-600 mt-1">18m</div>
                <div className="text-[10px] text-blue-600 font-bold mt-1">Target &lt; 60m</div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-xs">
                <div className="text-[11px] font-bold text-[#64748B] uppercase">CSAT Score</div>
                <div className="text-2xl font-black text-emerald-600 mt-1">{avgCsat} / 5.0</div>
                <div className="text-[10px] text-emerald-700 font-bold mt-1">96.8% positive</div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-xs">
                <div className="text-[11px] font-bold text-[#64748B] uppercase">Net Promoter (NPS)</div>
                <div className="text-2xl font-black text-indigo-600 mt-1">+{npsData.score}</div>
                <div className="text-[10px] text-indigo-600 font-bold mt-1">76% Promoters</div>
              </div>
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-xs">
                <div className="text-[11px] font-bold text-[#64748B] uppercase">Financial Escalations</div>
                <div className="text-2xl font-black text-amber-600 mt-1">{financialEscalations.length}</div>
                <div className="text-[10px] text-amber-700 font-bold mt-1">Prompt 23 Verified</div>
              </div>
            </div>

            {/* Architecture Principles Banner */}
            <div className="bg-linear-to-r from-slate-900 to-indigo-950 text-white rounded-2xl p-6 border border-slate-800 shadow-md">
              <div className="flex items-center gap-3 mb-3">
                <ShieldAlert className="w-5 h-5 text-amber-400" />
                <h2 className="text-base font-black tracking-wide">
                  YamooPay Invariant Guarantees & Support Non-Negotiables
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs text-slate-300">
                <div className="bg-slate-800/60 p-3 rounded-xl border border-slate-700">
                  <strong className="text-white block mb-1">Financial Calculation Disputes</strong>
                  If a user questions a tax or loan calculation, agents NEVER assume the software is right. Every claim is verified against statutory ruleset snapshots.
                </div>
                <div className="bg-slate-800/60 p-3 rounded-xl border border-slate-700">
                  <strong className="text-white block mb-1">Strict Zero-Secret Collection</strong>
                  No agent, form or AI copilot will ever ask for passwords, passkey secrets, full credit card numbers or CVC.
                </div>
                <div className="bg-slate-800/60 p-3 rounded-xl border border-slate-700">
                  <strong className="text-white block mb-1">AI Copilot Guardrails</strong>
                  AI drafts can assist agents via approved RAG knowledge, but autonomous auto-send is strictly prohibited on high-risk financial, billing or legal inquiries.
                </div>
              </div>
            </div>

            {/* Support Queues Overview */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h2 className="text-base font-black text-[#0F172A] mb-4 flex items-center justify-between">
                <span>Active Support Queues & SLA Status</span>
                <button
                  onClick={() => setActiveTab('queues')}
                  className="text-xs font-bold text-blue-600 hover:text-blue-800"
                >
                  Manage All Queues →
                </button>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {queues.slice(0, 6).map(q => (
                  <div key={q.id} className="border border-[#E2E8F0] rounded-xl p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-bold text-[#0F172A]">{q.name}</span>
                      {q.isHighRisk && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 bg-rose-50 text-rose-700 rounded">
                          High-Risk
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-[#64748B] mb-3 line-clamp-2">{q.description}</p>
                    <div className="flex items-center justify-between text-[11px] font-medium text-[#475569] pt-2 border-t border-[#F1F5F9]">
                      <span>Agents: <strong>{q.assignedAgents}</strong></span>
                      <span>Open: <strong className="text-blue-600">{q.openCases}</strong></span>
                      <span>SLA: <strong>{q.slaTargetMinutes}m</strong></span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* 2. SUPPORT CASES TAB */}
        {/* ==================================================== */}
        {activeTab === 'cases' && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Case List Column */}
            <div className="lg:col-span-5 space-y-4">
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-4 shadow-xs space-y-3">
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-3 text-[#94A3B8]" />
                  <input
                    type="text"
                    value={searchFilter}
                    onChange={e => setSearchFilter(e.target.value)}
                    placeholder="Search by Case ID, name, keyword..."
                    className="w-full pl-9 pr-3 py-2 border border-[#CBD5E1] rounded-xl text-xs outline-none focus:border-blue-600"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <select
                    value={queueFilter}
                    onChange={e => setQueueFilter(e.target.value)}
                    className="w-full px-2 py-1.5 border border-[#CBD5E1] rounded-lg text-xs outline-none bg-white text-[#334155]"
                  >
                    <option value="ALL">All Queues ({cases.length})</option>
                    {queues.map(q => (
                      <option key={q.id} value={q.id}>{q.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-3">
                {filteredCases.map(c => (
                  <div
                    key={c.id}
                    onClick={() => setSelectedCase(c)}
                    className={`bg-white border rounded-2xl p-4 cursor-pointer transition-all ${
                      selectedCase?.id === c.id
                        ? 'border-blue-600 ring-2 ring-blue-100 shadow-sm'
                        : 'border-[#E2E8F0] hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs font-bold text-blue-600">{c.publicId}</span>
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                          c.priority === 'P0_CRITICAL'
                            ? 'bg-rose-100 text-rose-800'
                            : c.priority === 'P1_HIGH'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-slate-100 text-slate-700'
                        }`}
                      >
                        {c.priority}
                      </span>
                    </div>
                    <h3 className="text-xs font-bold text-[#0F172A] line-clamp-1 mb-1">{c.subject}</h3>
                    <p className="text-[11px] text-[#64748B] line-clamp-2 mb-2">
                      {c.messages[0]?.content}
                    </p>
                    <div className="flex items-center justify-between text-[10px] font-medium text-[#94A3B8] pt-2 border-t border-[#F8FAFC]">
                      <span>{c.customerName} ({c.market})</span>
                      <span className="font-bold text-slate-700">{c.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Case Detail Column */}
            <div className="lg:col-span-7">
              {selectedCase ? (
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs space-y-6">
                  {/* Case Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-[#E2E8F0]">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-mono text-sm font-extrabold text-blue-600">{selectedCase.publicId}</span>
                        <span className="text-xs font-bold px-2 py-0.5 bg-blue-50 text-blue-700 rounded-md">
                          {selectedCase.domain}
                        </span>
                        <span className="text-xs font-bold px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md">
                          {selectedCase.market} ({selectedCase.locale})
                        </span>
                      </div>
                      <h2 className="text-base font-bold text-[#0F172A]">{selectedCase.subject}</h2>
                    </div>

                    <div className="flex items-center gap-2">
                      <select
                        value={selectedCase.status}
                        onChange={e => handleStatusChange(selectedCase.id, e.target.value as SupportCase['status'])}
                        className="px-3 py-1.5 border border-[#CBD5E1] rounded-lg text-xs font-bold bg-white text-[#0F172A] outline-none"
                      >
                        <option value="NEW">NEW</option>
                        <option value="OPEN">OPEN</option>
                        <option value="WAITING_FOR_CUSTOMER">WAITING_FOR_CUSTOMER</option>
                        <option value="ESCALATED">ESCALATED</option>
                        <option value="RESOLVED">RESOLVED</option>
                        <option value="CLOSED">CLOSED</option>
                      </select>
                    </div>
                  </div>

                  {/* Customer Safe Profile (Minimized Data) */}
                  <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl p-3 text-xs flex flex-wrap items-center justify-between gap-2">
                    <div>
                      Customer: <strong>{selectedCase.customerName}</strong> ({selectedCase.customerEmail})
                    </div>
                    <div>Plan: <strong className="text-indigo-600">{selectedCase.customerPlan}</strong></div>
                    <div>Channel: <strong>{selectedCase.channel}</strong></div>
                  </div>

                  {/* Contextual Link Badges */}
                  {(selectedCase.financialEscalationId || selectedCase.developerRequestId || selectedCase.billingTransactionRef || selectedCase.privacyRequestId) && (
                    <div className="flex flex-wrap gap-2">
                      {selectedCase.financialEscalationId && (
                        <div className="px-3 py-1.5 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg text-xs font-bold flex items-center gap-1.5">
                          <FileSpreadsheet className="w-3.5 h-3.5 text-amber-600" />
                          Financial Escalation: {selectedCase.financialEscalationId}
                        </div>
                      )}
                      {selectedCase.developerRequestId && (
                        <div className="px-3 py-1.5 bg-indigo-50 border border-indigo-200 text-indigo-900 rounded-lg text-xs font-mono font-bold flex items-center gap-1.5">
                          <Cpu className="w-3.5 h-3.5 text-indigo-600" />
                          Request ID: {selectedCase.developerRequestId}
                        </div>
                      )}
                      {selectedCase.billingTransactionRef && (
                        <div className="px-3 py-1.5 bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg text-xs font-bold flex items-center gap-1.5">
                          <CreditCard className="w-3.5 h-3.5 text-emerald-600" />
                          Txn Ref: {selectedCase.billingTransactionRef}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Conversation Messages */}
                  <div className="space-y-3">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#64748B]">
                      Conversation History & Internal Notes
                    </h4>
                    <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                      {selectedCase.messages.map(msg => (
                        <div
                          key={msg.id}
                          className={`p-4 rounded-xl text-xs leading-relaxed ${
                            msg.senderType === 'CUSTOMER'
                              ? 'bg-slate-100 text-[#1E293B]'
                              : msg.senderType === 'INTERNAL_NOTE'
                              ? 'bg-amber-50 border border-amber-200 text-amber-950 font-mono'
                              : 'bg-blue-50 border border-blue-200 text-blue-950'
                          }`}
                        >
                          <div className="flex items-center justify-between font-bold mb-1.5">
                            <span>
                              {msg.senderName} ({msg.senderType})
                            </span>
                            <span className="text-[10px] text-[#94A3B8]">
                              {new Date(msg.timestamp).toLocaleTimeString()}
                            </span>
                          </div>
                          <p className="whitespace-pre-line">{msg.content}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* AI Copilot Suggestion Box */}
                  <div className="bg-linear-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-blue-900">
                        <Sparkles className="w-4 h-4 text-blue-600" />
                        AI Support Copilot (RAG-Validated Suggestion)
                      </div>
                      <button
                        onClick={() => handleApplyAICopilot(selectedCase)}
                        className="px-2.5 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-[11px] font-bold transition-colors cursor-pointer"
                      >
                        Insert Suggestion
                      </button>
                    </div>
                    <p className="text-[11px] text-[#475569] leading-relaxed">
                      AI evaluated scenario against verified statutory rulesets and knowledge bases.
                    </p>
                  </div>

                  {/* Reply Editor */}
                  <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setReplyType('AGENT')}
                          className={`px-3 py-1 text-xs font-bold rounded-lg transition-colors cursor-pointer ${
                            replyType === 'AGENT'
                              ? 'bg-blue-600 text-white'
                              : 'bg-slate-100 text-[#475569]'
                          }`}
                        >
                          Public Reply to Customer
                        </button>
                        <button
                          type="button"
                          onClick={() => setReplyType('INTERNAL_NOTE')}
                          className={`px-3 py-1 text-xs font-bold rounded-lg transition-colors cursor-pointer ${
                            replyType === 'INTERNAL_NOTE'
                              ? 'bg-amber-600 text-white'
                              : 'bg-slate-100 text-[#475569]'
                          }`}
                        >
                          Internal Team Note
                        </button>
                      </div>
                    </div>

                    <textarea
                      rows={4}
                      value={replyMessage}
                      onChange={e => setReplyMessage(e.target.value)}
                      placeholder={
                        replyType === 'AGENT'
                          ? 'Compose public message to customer...'
                          : 'Add internal engineering or QA notes...'
                      }
                      className="w-full p-3 border border-[#CBD5E1] rounded-xl text-xs outline-none focus:border-blue-600"
                    />

                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => handleSendReply(selectedCase.id)}
                        className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                      >
                        <Send className="w-3.5 h-3.5" />
                        Send {replyType === 'AGENT' ? 'Message' : 'Internal Note'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white border border-[#E2E8F0] rounded-2xl p-12 text-center text-[#64748B]">
                  <MessageSquare className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <h4 className="text-sm font-bold text-[#0F172A]">Select a Support Case</h4>
                  <p className="text-xs mt-1">Choose a ticket from the left panel to inspect conversation, SLA and triggers.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* 3. SUPPORT QUEUES TAB */}
        {/* ==================================================== */}
        {activeTab === 'queues' && (
          <div className="space-y-6">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h2 className="text-base font-bold text-[#0F172A] mb-4">
                YamooPay Support Queue Architecture & Specialized Desks
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {queues.map(q => (
                  <div key={q.id} className="border border-[#E2E8F0] rounded-xl p-5 bg-white shadow-2xs flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded">
                          {q.tier}
                        </span>
                        {q.isHighRisk && (
                          <span className="text-xs font-bold text-rose-700 bg-rose-50 px-2 py-0.5 rounded">
                            High Risk Policy
                          </span>
                        )}
                      </div>
                      <h3 className="text-sm font-bold text-[#0F172A] mb-1">{q.name}</h3>
                      <p className="text-xs text-[#64748B] mb-4">{q.description}</p>
                    </div>
                    <div className="pt-3 border-t border-[#F1F5F9] grid grid-cols-3 text-center text-xs">
                      <div>
                        <span className="text-[#94A3B8] block text-[10px]">Agents</span>
                        <strong>{q.assignedAgents}</strong>
                      </div>
                      <div>
                        <span className="text-[#94A3B8] block text-[10px]">Open</span>
                        <strong className="text-blue-600">{q.openCases}</strong>
                      </div>
                      <div>
                        <span className="text-[#94A3B8] block text-[10px]">SLA Target</span>
                        <strong>{q.slaTargetMinutes}m</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* 4. FINANCIAL CALCULATION ESCALATIONS TAB */}
        {/* ==================================================== */}
        {activeTab === 'financial-escalations' && (
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-start gap-3">
                <FileSpreadsheet className="w-6 h-6 text-amber-700 shrink-0 mt-1" />
                <div>
                  <h3 className="text-sm font-bold text-amber-950">
                    YamooPay Financial Calculation & Statutory Audit Desk (Prompt 23 Integration)
                  </h3>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                    Whenever an end-user claims a calculation discrepancy in Salary PAYE, Mortgage APR, FX Margins, or FIRE projections, support automatically captures immutable snapshot parameters and escalates directly to Chief Quality / Tax Actuaries.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {financialEscalations.map(fce => (
                <div key={fce.id} className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-[#E2E8F0]">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-black text-amber-700">{fce.id}</span>
                        <span className="text-xs font-bold px-2 py-0.5 bg-slate-100 text-slate-700 rounded">
                          Tool: {fce.tool}
                        </span>
                        <span className="text-xs font-bold px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                          Jurisdiction: {fce.jurisdiction}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs font-bold px-3 py-1 bg-amber-100 text-amber-900 rounded-full">
                      Status: {fce.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                    <div className="bg-slate-50 p-4 rounded-xl space-y-1 font-mono">
                      <div className="text-[11px] font-bold text-[#64748B] uppercase font-sans mb-2">
                        Inputs Snapshot
                      </div>
                      {Object.entries(fce.inputSnapshot).map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span className="text-slate-500">{k}:</span>
                          <strong className="text-slate-800">{String(v)}</strong>
                        </div>
                      ))}
                    </div>

                    <div className="bg-slate-50 p-4 rounded-xl space-y-1 font-mono">
                      <div className="text-[11px] font-bold text-[#64748B] uppercase font-sans mb-2">
                        System Output Snapshot
                      </div>
                      {Object.entries(fce.systemOutputSnapshot).map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span className="text-slate-500">{k}:</span>
                          <strong className="text-emerald-700">{String(v)}</strong>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="text-xs bg-amber-50/50 p-3 rounded-xl border border-amber-200/50">
                    <strong>Customer Claim:</strong> {fce.customerClaimedDiscrepancy}
                  </div>

                  <div className="flex items-center justify-between text-xs text-[#64748B] pt-2 border-t border-[#F8FAFC]">
                    <span>Validator: <strong>{fce.validatorName || 'Unassigned'}</strong></span>
                    <span>Ruleset: <strong>{fce.rulesetVersion}</strong></span>
                    {fce.linkedQualityDefectId && (
                      <span className="text-rose-600 font-bold">
                        Linked Defect: {fce.linkedQualityDefectId}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* 5. AI SUPPORT COPILOT & RAG TAB */}
        {/* ==================================================== */}
        {activeTab === 'ai-copilot' && (
          <div className="space-y-6">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <div className="flex items-center gap-3 mb-4">
                <BrainCircuit className="w-6 h-6 text-blue-600" />
                <div>
                  <h2 className="text-base font-bold text-[#0F172A]">
                    YamooPay Support AI Copilot Governance & Safety Boundary
                  </h2>
                  <p className="text-xs text-[#64748B]">
                    Strict RAG grounding on approved Knowledge Base articles with human-in-the-loop review.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="border border-[#E2E8F0] rounded-xl p-4 space-y-3">
                  <h3 className="text-xs font-bold text-[#0F172A] uppercase tracking-wider">
                    Permitted AI Capabilities
                  </h3>
                  <ul className="text-xs space-y-2 text-[#334155]">
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      Semantic search across published Knowledge Base & statutory rulesets
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      Draft reply generation for agent review (with citation badges)
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      Automatic language detection & lossless translation assistance
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      Automatic extraction of Developer Request IDs from payloads
                    </li>
                  </ul>
                </div>

                <div className="border border-[#E2E8F0] rounded-xl p-4 space-y-3 bg-rose-50/50">
                  <h3 className="text-xs font-bold text-rose-900 uppercase tracking-wider">
                    Prohibited Autonomous AI Actions
                  </h3>
                  <ul className="text-xs space-y-2 text-rose-800">
                    <li className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-rose-600 shrink-0" />
                      AI CANNOT issue refunds or directly modify billing ledgers
                    </li>
                    <li className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-rose-600 shrink-0" />
                      AI CANNOT unilaterally close formal GDPR/CCPA privacy requests
                    </li>
                    <li className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-rose-600 shrink-0" />
                      AI CANNOT modify tax rulesets or declare math bugs resolved
                    </li>
                    <li className="flex items-center gap-2">
                      <Lock className="w-4 h-4 text-rose-600 shrink-0" />
                      AI CANNOT expose internal staff runbooks to public consumers
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* 6. KNOWLEDGE GAPS TAB */}
        {/* ==================================================== */}
        {activeTab === 'knowledge-gaps' && (
          <div className="space-y-6">
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h2 className="text-base font-bold text-[#0F172A] mb-4">
                Automated Knowledge Gap Detection (Zero-Result Searches & Ticket Clusters)
              </h2>
              <div className="space-y-4">
                {knowledgeGaps.map(gap => (
                  <div key={gap.id} className="border border-[#E2E8F0] rounded-xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold px-2 py-0.5 bg-rose-50 text-rose-700 rounded">
                          {gap.priority}
                        </span>
                        <span className="text-xs font-bold text-[#0F172A]">{gap.searchQueryCluster}</span>
                      </div>
                      <p className="text-xs text-[#64748B]">{gap.suggestedAction}</p>
                    </div>

                    <div className="flex items-center gap-6 text-xs text-[#475569] shrink-0">
                      <div>
                        <span className="text-[10px] text-[#94A3B8] block">30d Searches</span>
                        <strong>{gap.searchVolume30d}</strong>
                      </div>
                      <div>
                        <span className="text-[10px] text-[#94A3B8] block">Zero Results</span>
                        <strong className="text-rose-600">{gap.zeroResultCount}</strong>
                      </div>
                      <div>
                        <span className="text-[10px] text-[#94A3B8] block">Status</span>
                        <strong className="text-blue-600">{gap.status}</strong>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* 7. VOICE OF CUSTOMER & CSAT/NPS TAB */}
        {/* ==================================================== */}
        {activeTab === 'voc-analytics' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* CSAT Card */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs space-y-4">
                <h2 className="text-sm font-bold text-[#0F172A] flex items-center justify-between">
                  <span>Customer Satisfaction (CSAT) Distribution</span>
                  <span className="text-emerald-600 font-extrabold text-base">{avgCsat} / 5.0</span>
                </h2>
                <div className="space-y-2">
                  {csatList.map(csat => (
                    <div key={csat.id} className="bg-slate-50 p-3 rounded-xl text-xs space-y-1">
                      <div className="flex items-center justify-between font-bold">
                        <span>{csat.casePublicId} ({csat.category})</span>
                        <span className="text-amber-500">{'★'.repeat(csat.score)}</span>
                      </div>
                      <p className="text-[#64748B] italic">&quot;{csat.comment}&quot;</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* NPS Card */}
              <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs space-y-4">
                <h2 className="text-sm font-bold text-[#0F172A] flex items-center justify-between">
                  <span>Net Promoter Score (NPS) by Market</span>
                  <span className="text-indigo-600 font-extrabold text-base">+{npsData.score}</span>
                </h2>
                <div className="grid grid-cols-3 gap-2 text-center text-xs">
                  <div className="p-3 bg-emerald-50 text-emerald-900 rounded-xl font-bold">
                    Promoters: {npsData.promotersPct}%
                  </div>
                  <div className="p-3 bg-slate-50 text-slate-900 rounded-xl font-bold">
                    Passives: {npsData.passivesPct}%
                  </div>
                  <div className="p-3 bg-rose-50 text-rose-900 rounded-xl font-bold">
                    Detractors: {npsData.detractorsPct}%
                  </div>
                </div>
                <div className="space-y-2 pt-2">
                  {Object.entries(npsData.byMarket).map(([m, score]) => (
                    <div key={m} className="flex items-center justify-between text-xs">
                      <span className="text-[#64748B]">Market {m}</span>
                      <strong className="text-indigo-700">+{score} NPS</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Voice of Customer Themes */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
              <h2 className="text-base font-bold text-[#0F172A] mb-4">
                Voice of Customer (VoC) Strategic Product Signals
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {vocThemes.map(voc => (
                  <div key={voc.id} className="border border-[#E2E8F0] rounded-xl p-4 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] font-bold px-2 py-0.5 bg-blue-50 text-blue-700 rounded">
                          {voc.domain}
                        </span>
                        <span className="text-[10px] font-bold text-slate-600">
                          Impact: {voc.impactScore}/100
                        </span>
                      </div>
                      <h3 className="text-xs font-bold text-[#0F172A] mb-2">{voc.themeName}</h3>
                      <div className="text-[11px] text-[#64748B] italic space-y-1 mb-3">
                        {voc.representativeQuotes.map((q, idx) => (
                          <div key={idx}>&quot;{q}&quot;</div>
                        ))}
                      </div>
                    </div>
                    <div className="pt-2 border-t border-[#F8FAFC] text-[11px]">
                      <span className="text-[#94A3B8] block">Product Roadmap Action:</span>
                      <strong className="text-blue-600">{voc.productActionItem}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ==================================================== */}
        {/* 8. SUPPORT AUDIT LOG TAB */}
        {/* ==================================================== */}
        {activeTab === 'audit-log' && (
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6 shadow-xs">
            <h2 className="text-base font-bold text-[#0F172A] mb-4">
              Immutable Support Operations Audit Trail (Prompt 17/22 Compliant)
            </h2>
            <div className="space-y-3">
              {auditLog.map(entry => (
                <div key={entry.id} className="p-3 border border-[#E2E8F0] rounded-xl text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-blue-600">{entry.casePublicId}</span>
                      <span className="font-bold text-[#0F172A]">{entry.action}</span>
                    </div>
                    <p className="text-[#64748B] mt-0.5">{entry.details}</p>
                  </div>
                  <div className="text-right text-[11px] text-[#94A3B8] shrink-0">
                    <div>{entry.actor}</div>
                    <div>{new Date(entry.timestamp).toLocaleTimeString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
